import { guardUnspecified } from '@portal/utils/util-guards';
import { isBrowser } from '@portal/utils/util-is-in-browser';
import dayjs, { ManipulateType, OpUnitType, locale } from 'dayjs';
import ru from 'dayjs/locale/ru';

import { UtilDateInvalidDateArgumentError } from './util-date-invalid-date-argument-error';

locale(ru);

const DAY_IN_YEAR = 365.24;
const MS_IN_DAY = 86400000;
const MS_IN_SEC = 1000;

const isValidDate = (date: DateType): boolean => dayjs(date).isValid();

const dateValidateChecker = (date: DateType, message: string): void => {
  if (!isValidDate(date)) {
    throw new UtilDateInvalidDateArgumentError(message);
  }
};

const getSafariDate = (date: DateType): Date => {
  const value = new Date(date);
  const msPerMinute = 60000;
  const tzOffset = value.getTimezoneOffset() * msPerMinute;
  return new Date(value.getTime() + tzOffset);
};

const checkOldSafari = (): boolean => {
  const latestSafariVersion = 14;
  const userAgent = navigator.userAgent.toLowerCase();
  const matchVersion = new RegExp('.*version/([^.]+).*').exec(userAgent);
  const currentVersion =
    matchVersion !== null ? Number(matchVersion[1]) : latestSafariVersion;

  return (
    /safari/.test(navigator.userAgent.toLowerCase()) &&
    currentVersion < 14 &&
    !/chrome/.test(navigator.userAgent.toLowerCase())
  );
};

const isOldSafari = () => (isBrowser() ? checkOldSafari() : false);

export type DateType = Date | number | string;

export type DurationType = {
  value: number;
  unit?: ManipulateType;
};

export const dateFormat = (date: DateType, format = 'YYYY-MM-DD'): string => {
  dateValidateChecker(date, 'date is invalid');
  const value = isOldSafari() ? getSafariDate(date) : date;
  return dayjs(value).format(format);
};

export const differenceInCalendarDays = (
  dateEnd: DateType,
  dateStart: DateType
): number => {
  dateValidateChecker(dateEnd, 'dateEnd is invalid');
  dateValidateChecker(dateStart, 'dateStart is invalid');

  const dateLeft = dayjs(dateFormat(dateEnd)).valueOf();
  const dateRight = dayjs(dateFormat(dateStart)).valueOf();
  return Math.floor(Math.abs((dateLeft - dateRight) / MS_IN_DAY));
};

export const differenceInYears = (dateEnd: DateType, dateStart: DateType): number => {
  dateValidateChecker(dateEnd, 'dateEnd is invalid');
  dateValidateChecker(dateStart, 'dateStart is invalid');

  const diffDay = differenceInCalendarDays(dateEnd, dateStart);
  return Math.floor(diffDay / DAY_IN_YEAR);
};

export const differenceInSeconds = (dateEnd: DateType, dateStart: DateType): number => {
  dateValidateChecker(dateEnd, 'dateEnd is invalid');
  dateValidateChecker(dateStart, 'dateStart is invalid');

  const dateLeft = dayjs(dateEnd).valueOf();
  const dateRight = dayjs(dateStart).valueOf();
  return Math.floor(Math.abs((dateLeft - dateRight) / MS_IN_SEC));
};

export const isBefore = (
  date: DateType,
  dateToCompare: DateType,
  limit?: OpUnitType
): boolean => {
  dateValidateChecker(date, 'date is invalid');
  dateValidateChecker(dateToCompare, 'dateToCompare is invalid');

  return dayjs(date).isBefore(dateToCompare, limit);
};

export const isAfter = (
  date: DateType,
  dateToCompare: DateType,
  limit?: OpUnitType
): boolean => {
  dateValidateChecker(date, 'date is invalid');
  dateValidateChecker(dateToCompare, 'dateToCompare is invalid');

  return dayjs(date).isAfter(dateToCompare, limit);
};

export const subtract = (
  date: DateType,
  duration: DurationType,
  format = 'YYYY-MM-DD'
): string => {
  dateValidateChecker(date, 'date is invalid');

  if (!guardUnspecified(duration.value)) {
    throw new UtilDateInvalidDateArgumentError('duration value in invalid');
  }

  if (!guardUnspecified(duration.unit)) {
    throw new UtilDateInvalidDateArgumentError('duration unit in invalid');
  }

  return dayjs(date).subtract(duration.value, duration.unit).format(format);
};

export const daysInMonth = (date: DateType): number => {
  dateValidateChecker(date, 'date is invalid');

  return dayjs(date).daysInMonth();
};

export const formatDateWithMonthName = (date: DateType, showTime = true): string => {
  dateValidateChecker(date, 'date is invalid');

  if (showTime) {
    return dateFormat(date, 'D MMM YYYY в HH:mm').replace('.', '');
  }
  return dateFormat(date, 'D MMM YYYY').replace('.', '');
};
